import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register the thresholdBackgroundPlugin locally (not globally)
const thresholdBackgroundPlugin = {
    id: 'thresholdBackground',
    beforeDraw: (chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        
        // Just fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(
            chartArea.left, 
            chartArea.top, 
            chartArea.right - chartArea.left, 
            chartArea.bottom - chartArea.top
        );
    }
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    annotationPlugin,
    thresholdBackgroundPlugin
);

// Updated getBarColor function for movement scores with threshold-based coloring
const getBarColor = (value, thresholds, zeroValue = false) => {
    // If it's a zero value or invalid, return a light gray color
    if (zeroValue || value === 0 || isNaN(value)) {
        return 'rgba(180, 180, 180, 0.5)'; // light gray for zero/missing values
    }

    // Special case for maximum score
    if (value >= 10) return '#90FF8C'; // Always green for max score
    
    if (value <= thresholds.low) return '#FF8A8A'; // Red (very low)
    if (value <= thresholds.normalLow) return '#FFE566'; // Yellow (low)
    if (value <= thresholds.normalHigh) return '#90FF8C'; // Green (normal)
    return '#90FF8C'; // Green (wanted above average movement)
};

const ModularTrendsSpecificThreshholdChart = ({ title, metricLabel, historyData, thresholds, minValue, maxValue }) => {
    // Format date for x-axis labels
    const formatDateLabel = (dateString) => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.warn('Invalid date:', dateString);
                return '';
            }
            
            const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
            const monthDay = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
            
            return `${weekday}\n${monthDay}`;
        } catch (error) {
            console.error('Date parsing error:', error);
            return '';
        }
    };

    // Extract values and dates from historyData
    const processedData = React.useMemo(() => {
        if (!Array.isArray(historyData)) return { values: [], dates: [] };

        // Process data and then reverse both arrays to show oldest to newest
        const processed = historyData.reduce((acc, item) => {
            let value = typeof item === 'object' ? parseFloat(item.value) : parseFloat(item);
            let date = typeof item === 'object' ? item.period_start : null;
            
            // Handle time format like "8h 30m" for sleep data
            if (typeof item === 'object' && item.value && typeof item.value === 'string') {
                const timeMatch = item.value.match(/^(\d{1,2})h\s(\d{1,2})m$/);
                if (timeMatch) {
                    // Convert hours and minutes to total minutes for the chart
                    const hours = parseInt(timeMatch[1], 10);
                    const minutes = parseInt(timeMatch[2], 10);
                    value = hours * 60 + minutes; // Convert to total minutes
                }
            }
            
            acc.values.push(isNaN(value) ? 0 : value);
            acc.dates.push(date ? formatDateLabel(date) : '');

            return acc;
        }, { values: [], dates: [] });

        // Reverse both arrays to show oldest to newest (left to right)
        return {
            values: processed.values.reverse(),
            dates: processed.dates.reverse()
        };
    }, [historyData]);

    // Determine if a value is zero for coloring purposes
    const zeroValues = processedData.values.map(value => value === 0);

    // Note: We don't need to pre-calculate barColors here anymore
    // as we're directly mapping colors in the dataset configuration below

    // Calculate full range for thresholds to ensure all zones are visible
    const getFullThresholdRange = () => {
        // Always use 1-10 scale for movement data
        return {
            min: 1,  // Start at 1 (poor)
            max: 10  // End at 10 (best)
        };
    };

    const thresholdRange = getFullThresholdRange();
    
    // Use threshold range directly for chart min/max
    const effectiveMinValue = thresholdRange.min;
    const effectiveMaxValue = thresholdRange.max;

    // Calculate gradient stops based on full range
    const getPercentage = (value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 0;
        const range = effectiveMaxValue - effectiveMinValue;
        // Invert the percentage calculation to match chart orientation
        return Math.min(Math.max((100 - ((numValue - effectiveMinValue) / range * 100)), 0), 100).toFixed(1);
    };

    // Ensure all threshold values are numbers with reasonable defaults for movement score
    const validThresholds = {
        low: Math.max(1, parseFloat(thresholds.low) || 3.5),        // Below 3.5 is poor
        normalLow: Math.max(1, parseFloat(thresholds.normalLow) || 4.5),  // 3.5-4.5 is fair
        average: parseFloat(thresholds.average) || 5.0,            // Average is shown as 5.0 in screenshot
        normalHigh: Math.min(10, parseFloat(thresholds.normalHigh) || 8.0), // Above 8.0 is excellent
        high: Math.min(10, parseFloat(thresholds.high) || 9.0),      // Since 10 is max, this is capped
        status: thresholds.status || 'Unknown'
    };

    const calculateDailyStatuses = (historyData, validThresholds) => {
        // Check if we have valid data to process
        if (!historyData || !Array.isArray(historyData) || historyData.length === 0) {
            return ['No Data'];
        }

        const statusList = historyData.map(data => {
            // Handle null/undefined values or missing value property
            if (!data || data.value === null || data.value === undefined) {
                return 'No Data';
            }

            // Parse the value if it's a sleep time format (e.g., "8h 30m")
            let value = data.value;
            if (typeof value === 'string' && value.match(/^\d{1,2}h\s\d{1,2}m$/)) {
                value = parseTimeToMinutes(value);
            }

            // Evaluate against thresholds to determine status
            if (value <= validThresholds.low) return 'Caution';
            if (value <= validThresholds.normalLow) return 'Attention';
            if (value <= validThresholds.normalHigh) return 'Good';
            if (value <= validThresholds.high) return 'Attention';
            return 'Caution';
        });
        return statusList;
    }
    
    const statusList = calculateDailyStatuses(historyData, validThresholds);
    
    const calculateWeeklyStatus = (statusList) => {
        // If we have no status list or it's empty, return 'No Data'
        if (!statusList || statusList.length === 0) {
            return 'No Data';
        }
        
        // If there is not enough data, return 'Insufficient data'
        const noDataCount = statusList.filter(status => status === 'No Data').length;
        if (noDataCount >= 3 || statusList.length < 3) return 'Insufficient data';

        // Calculate weekly status based on the most common status of the week
        const weeklyStatusCounts = statusList.reduce((acc, status) => {
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
        
        // If all counts are equal, prioritize in this order: Good > Attention > Caution
        const statusPriority = { 'Good': 3, 'Attention': 2, 'Caution': 1, 'No Data': 0 };
        
        const mostCommonStatus = Object.keys(weeklyStatusCounts).reduce((a, b) => {
            // If counts are the same, choose the one with higher priority
            if (weeklyStatusCounts[a] === weeklyStatusCounts[b]) {
                return statusPriority[a] > statusPriority[b] ? a : b;
            }
            // Otherwise choose the one with higher count
            return weeklyStatusCounts[a] > weeklyStatusCounts[b] ? a : b;
        });
        
        return mostCommonStatus;
    }
    
    const weeklyStatus = calculateWeeklyStatus(statusList);

    const data = {
        labels: processedData.dates,
        datasets: [
            {
                type: "line",
                label: "Trend",
                data: processedData.values,
                borderColor: "#3498db",
                borderWidth: 4,
                tension: 0.4,
                fill: false,
                pointBackgroundColor: "#3498db",
                pointRadius: 4,
                order: 1,
                datalabels: {
                    display: false
                },
                // Custom legend styling for line
                pointStyle: 'line',
                pointBorderWidth: 2
            },
            {
                label: metricLabel,
                data: processedData.values,
                type: "bar",
                backgroundColor: 'rgba(200, 200, 200, 0.7)',
                borderColor: "#4B4B4D",
                borderWidth: 1,
                order: 2,
                // Legend styling only (won't affect the actual bars)
                legendPointStyle: 'rect',
                legendBorderColor: 'black',
                legendBackgroundColor: 'transparent'
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 0,
                top: 30,
                right: 20,
                bottom: 10
            }
        },
        scales: {
            y: {
                grid: {
                    display: true
                },
                title: {
                    display: true,
                    text: 'Movement Score (1-10)',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                min: 0,
                max: 10,
                suggestedMax: 10,
                suggestedMin: 0,
                stacked: false,
                ticks: {
                    callback: function(value) {
                        // Show decimal for movement score
                        return value.toFixed(1);
                    },
                    // Add key threshold values to ensure they appear on the axis
                    includeBounds: true,
                    // Force include the threshold values
                    afterBuildTicks: function(scale) {
                        // Include both whole numbers and threshold values
                        const baseTickValues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
                        const thresholdValues = [
                            validThresholds.low,
                            validThresholds.normalLow, 
                            validThresholds.normalHigh,
                            validThresholds.high
                        ];
                        
                        // Combine and remove duplicates
                        const allValues = [...new Set([...baseTickValues, ...thresholdValues])];
                        
                        // Sort values and convert to tick objects
                        return allValues.sort((a, b) => a - b).map(value => ({ value }));
                    }
                },
                beginAtZero: true
            },
            x: {
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Daily Reports',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    font: {
                        size: 11
                    },
                    padding: 10
                }
            }
        },
        plugins: {
            datalabels: {
                display: true,
                color: 'black',
                anchor: 'end',
                align: 'top',
                offset: -2,
                formatter: function(value) {
                    return formatDuration(Math.max(0, value));
                },
                font: {
                    weight: 'bold',
                    size: 11
                }
            },
            legend: {
                display: true,
                position: 'top',
                align: 'center',
                onHover: (event, legendItem, legend) => {
                    const tooltips = {
                        'Movement Score': 'Movement Score Calculated',
                        'Trend': 'Trend of the metric'
                    };
                    
                    // Change cursor to indicate interactivity
                    event.native.target.style.cursor = 'pointer';
                    
                    // Find tooltip container or create it if it doesn't exist
                    let tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chart-legend-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = 'rgba(0,0,0,0.7)';
                        tooltipEl.style.color = 'white';
                        tooltipEl.style.padding = '5px 10px';
                        tooltipEl.style.borderRadius = '3px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.transition = 'opacity 0.2s';
                        tooltipEl.style.zIndex = '10';
                        document.body.appendChild(tooltipEl);
                    }
                    
                    // Debug: Log the exact legend item text to see what we're working with
                    console.log('Legend item text:', JSON.stringify(legendItem.text));
                    console.log('Available tooltip keys:', Object.keys(tooltips));
                    
                    // Show tooltip with description
                    if (tooltips[legendItem.text]) {
                        tooltipEl.innerHTML = tooltips[legendItem.text];
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = event.native.pageX + 'px';
                        tooltipEl.style.top = (event.native.pageY - 28) + 'px'; // Position above the cursor
                    } else {
                        // Fallback: Try to find a close match for the text
                        const keys = Object.keys(tooltips);
                        const closeMatch = keys.find(key => 
                            key.toLowerCase().includes('night') && 
                            key.toLowerCase().includes('sleep'));
                        
                        if (closeMatch) {
                            tooltipEl.innerHTML = tooltips[closeMatch];
                            tooltipEl.style.opacity = '1';
                            tooltipEl.style.left = event.native.pageX + 'px';
                            tooltipEl.style.top = (event.native.pageY - 28) + 'px';
                            
                            // Log that we're using a close match
                            console.log('Using close match:', closeMatch, 'for', legendItem.text);
                        }
                    }
                },
                onLeave: (event, legendItem, legend) => {
                    // Reset cursor
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = 'default';
                    }
                    
                    // Hide tooltip
                    const tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (tooltipEl) {
                        tooltipEl.style.opacity = '0';
                    }
                },
                labels: {
                    boxWidth: 30,
                    padding: 15,
                    usePointStyle: true,
                    pointStyle: true,
                    font: {
                        size: 12,
                        weight: '500'
                    },
                    color: '#444',
                    generateLabels: function(chart) {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => ({
                            text: dataset.label,
                            fillStyle: i === 0 ? dataset.borderColor : 'transparent',
                            strokeStyle: i === 0 ? dataset.borderColor : 'black',
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                            // Use custom legend style for bars, but default for line
                            pointStyle: i === 0 ? dataset.pointStyle : dataset.legendPointStyle,
                            // Use custom colors for legend only
                            borderColor: i === 0 ? dataset.borderColor : dataset.legendBorderColor,
                            backgroundColor: i === 0 ? dataset.borderColor : dataset.legendBackgroundColor
                        }));
                    }
                }
            },
            title: {
                display: true,
                text: [title],
                font: {
                    size: 16,
                    weight: 'bold'
                }
            },
            thresholdBackground: false,
            annotation: {
                annotations: {
                    // lowLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.low,
                    //     yMax: validThresholds.low,
                    //     borderColor: '#FF8A8A', // Red
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // normalLowLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.normalLow,
                    //     yMax: validThresholds.normalLow,
                    //     borderColor: '#FFE566', // Yellow
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // }
                }
            }
        }
    };

    const formatDuration = (minutes) => {
        // For movement score, show with one decimal place
        return typeof minutes === 'number' ? minutes.toFixed(1) : '0.0';
    };

    const formatLabel = (value) => {
        if (typeof value === 'object' && value.value && value.unit) {
            return (
                <div style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                    <span>{value.value}</span>
                    <span style={{ fontSize: '0.7em', marginLeft: '2px', marginTop: '-2px' }}>{value.unit}</span>
                </div>
            );
        }
        return value;
    };

    return (
        <div style={{ position: 'relative', height: '600px', width: '100%', marginTop: '30px', marginBottom: '30px'  }}>
            <div style={{ height: '100%', width: '100%' }}>
                <Chart data={data} options={options} plugins={[thresholdBackgroundPlugin]} />
            </div>
        </div>
    );
};

export default ModularTrendsSpecificThreshholdChart;
